import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Download, 
  RefreshCw, 
  Settings, 
  Grid, 
  List, 
  Search, 
  Eye,
  Trash2,
  Clock,
  Image as ImageIcon,
  Loader2
} from 'lucide-react';
import { 
  imageGenerationService, 
  GeneratedImage, 
  ImageGenerationOptions 
} from '../services/imageGeneration';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/Badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';

interface ImageDashboardProps {
  className?: string;
}

interface GenerationSettings {
  style: ImageGenerationOptions['style'];
  size: ImageGenerationOptions['size'];
  quality: ImageGenerationOptions['quality'];
  autoGenerate: boolean;
}

export const ImageGenerationDashboard: React.FC<ImageDashboardProps> = ({ className = '' }) => {
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStyle, setSelectedStyle] = useState<string>('all');
  const [settings, setSettings] = useState<GenerationSettings>({
    style: 'realistic',
    size: 'landscape_16_9',
    quality: 'high',
    autoGenerate: true
  });
  const [showSettings, setShowSettings] = useState(false);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);
  const [cacheStats, setCacheStats] = useState({ totalCached: 0, totalSize: 0 });

  // Predefined prompts for different sections
  const sectionPrompts = {
    hero: [
      "Unified health ecosystem, fragmented apps coming together, modern technology interface",
      "Health transformation journey, before and after, wellness optimization",
      "Biotune loop visualization, continuous health improvement cycle, data flow"
    ],
    dashboard: [
      "Health dashboard interface, biomarker tracking, personalized analytics",
      "Mobile health app screenshots, clean UI design, user-friendly interface",
      "Wearable device integration, Apple Watch health data, fitness tracking"
    ],
    supplements: [
      "Premium supplement bottles, high-quality ingredients, scientific formulation",
      "Adaptogenic herbs, ashwagandha and rhodiola plants, natural wellness",
      "Anti-inflammatory compounds, turmeric and ginger, holistic health"
    ],
    nutrition: [
      "Personalized meal plans, nutrition optimization, healthy food arrangement",
      "Mediterranean diet foods, heart-healthy ingredients, fresh produce",
      "Brain-boosting foods, omega-3 rich meals, cognitive support nutrition"
    ],
    fitness: [
      "Personalized workout routines, fitness optimization, exercise tracking",
      "HIIT training visualization, heart rate zones, cardiovascular health",
      "Strength training programs, muscle development, hormone optimization"
    ],
    mindfulness: [
      "Meditation and mindfulness practices, stress reduction, mental wellness",
      "Sleep optimization environment, circadian rhythm support, restful bedroom",
      "HRV monitoring and stress management, biofeedback visualization"
    ]
  };

  // Load cached images on mount
  useEffect(() => {
    loadCachedImages();
    updateCacheStats();
  }, []);

  // Auto-generate images for different sections
  useEffect(() => {
    if (settings.autoGenerate && generatedImages.length === 0) {
      generateSectionImages();
    }
  }, [settings.autoGenerate]);

  const loadCachedImages = () => {
    // Load images from localStorage or service cache
    const cached = localStorage.getItem('biotune_generated_images');
    if (cached) {
      try {
        const images = JSON.parse(cached);
        setGeneratedImages(images);
      } catch (error) {
        console.error('Failed to load cached images:', error);
      }
    }
  };

  const saveToCache = (images: GeneratedImage[]) => {
    localStorage.setItem('biotune_generated_images', JSON.stringify(images));
  };

  const updateCacheStats = () => {
    const stats = imageGenerationService.getCacheStats();
    setCacheStats(stats);
  };

  const generateSectionImages = async () => {
    setIsGenerating(true);
    
    try {
      const allPrompts = Object.values(sectionPrompts).flat();
      const selectedPrompts = allPrompts.slice(0, 6); // Generate 6 images initially
      
      const generationPromises = selectedPrompts.map(prompt => 
        imageGenerationService.generateImage({
          prompt,
          style: settings.style,
          size: settings.size,
          quality: settings.quality,
          branding: true
        })
      );
      
      const newImages = await Promise.all(generationPromises);
      const updatedImages = [...generatedImages, ...newImages];
      
      setGeneratedImages(updatedImages);
      saveToCache(updatedImages);
      updateCacheStats();
      
    } catch (error) {
      console.error('Failed to generate section images:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const generateCustomImage = async (prompt: string) => {
    if (!prompt.trim()) return;
    
    setIsGenerating(true);
    
    try {
      const newImage = await imageGenerationService.generateImage({
        prompt: prompt.trim(),
        style: settings.style,
        size: settings.size,
        quality: settings.quality,
        branding: true
      });
      
      const updatedImages = [newImage, ...generatedImages];
      setGeneratedImages(updatedImages);
      saveToCache(updatedImages);
      updateCacheStats();
      
    } catch (error) {
      console.error('Failed to generate custom image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadImage = async (image: GeneratedImage) => {
    try {
      const response = await fetch(image.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `biotune-${image.id}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  };

  const deleteImage = (imageId: string) => {
    const updatedImages = generatedImages.filter(img => img.id !== imageId);
    setGeneratedImages(updatedImages);
    saveToCache(updatedImages);
    updateCacheStats();
  };

  const refreshImage = async (image: GeneratedImage) => {
    setIsGenerating(true);
    
    try {
      const newImage = await imageGenerationService.generateImage({
        prompt: image.prompt.replace(/\s*\([^)]*\)/g, ''), // Remove any previous generation metadata
        style: image.style as ImageGenerationOptions['style'],
        size: settings.size,
        quality: settings.quality,
        branding: true
      });
      
      const updatedImages = generatedImages.map(img => 
        img.id === image.id ? newImage : img
      );
      
      setGeneratedImages(updatedImages);
      saveToCache(updatedImages);
      updateCacheStats();
      
    } catch (error) {
      console.error('Failed to refresh image:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const clearAllImages = () => {
    setGeneratedImages([]);
    localStorage.removeItem('biotune_generated_images');
    imageGenerationService.clearCache();
    updateCacheStats();
  };

  const filteredImages = generatedImages.filter(image => {
    const matchesSearch = image.prompt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.altText.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStyle = selectedStyle === 'all' || image.style === selectedStyle;
    return matchesSearch && matchesStyle;
  });

  return (
    <div className={`min-h-screen bg-theme-bg ${className}`}>
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-theme-text-primary mb-4">
              AI Image Generation Dashboard
            </h1>
            <p className="text-xl text-theme-text-secondary max-w-3xl mx-auto">
              Create stunning visuals for your BioTune experience with our AI-powered image generation system
            </p>
          </motion.div>

          {/* Controls */}
          <Card className="glass-surface mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                {/* Search and Filter */}
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-theme-text-muted w-4 h-4" />
                    <Input
                      type="text"
                      placeholder="Search generated images..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-theme-surface-light border-theme-border text-theme-text-primary placeholder:text-theme-text-muted"
                    />
                  </div>
                  <Select value={selectedStyle} onValueChange={setSelectedStyle}>
                    <SelectTrigger className="w-[180px] bg-theme-surface-light border-theme-border text-theme-text-primary">
                      <SelectValue placeholder="Filter by style" />
                    </SelectTrigger>
                    <SelectContent className="bg-theme-surface border-theme-border">
                      <SelectItem value="all">All Styles</SelectItem>
                      <SelectItem value="realistic">Realistic</SelectItem>
                      <SelectItem value="illustrated">Illustrated</SelectItem>
                      <SelectItem value="minimalist">Minimalist</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="futuristic">Futuristic</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={() => generateSectionImages()}
                    disabled={isGenerating}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {isGenerating ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Generate Images
                  </Button>
                  <Button
                    onClick={() => setShowSettings(!showSettings)}
                    variant="outline"
                    className="border-theme-border text-theme-text-primary hover:bg-theme-surface-light"
                  >
                    <Settings className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                    variant="outline"
                    className="border-theme-border text-theme-text-primary hover:bg-theme-surface-light"
                  >
                    {viewMode === 'grid' ? <List className="w-4 h-4" /> : <Grid className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Settings Panel */}
              <AnimatePresence>
                {showSettings && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-4 pt-4 border-t border-theme-border"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">Style</label>
                        <Select 
                          value={settings.style} 
                          onValueChange={(value) => setSettings({...settings, style: value as any})}
                        >
                          <SelectTrigger className="bg-theme-surface-light border-theme-border text-theme-text-primary">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-theme-surface border-theme-border">
                            <SelectItem value="realistic">Realistic</SelectItem>
                            <SelectItem value="illustrated">Illustrated</SelectItem>
                            <SelectItem value="minimalist">Minimalist</SelectItem>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="futuristic">Futuristic</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">Size</label>
                        <Select 
                          value={settings.size} 
                          onValueChange={(value) => setSettings({...settings, size: value as any})}
                        >
                          <SelectTrigger className="bg-theme-surface-light border-theme-border text-theme-text-primary">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-theme-surface border-theme-border">
                            <SelectItem value="square">Square</SelectItem>
                            <SelectItem value="square_hd">Square HD</SelectItem>
                            <SelectItem value="portrait_4_3">Portrait</SelectItem>
                            <SelectItem value="landscape_16_9">Landscape</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-theme-text-secondary mb-2">Quality</label>
                        <Select 
                          value={settings.quality} 
                          onValueChange={(value) => setSettings({...settings, quality: value as any})}
                        >
                          <SelectTrigger className="bg-theme-surface-light border-theme-border text-theme-text-primary">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-theme-surface border-theme-border">
                            <SelectItem value="standard">Standard</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end">
                        <Button
                          onClick={clearAllImages}
                          variant="destructive"
                          className="w-full"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Clear All
                        </Button>
                      </div>
                    </div>
                    <div className="mt-4 text-sm text-theme-text-muted">
                      Cache Stats: {cacheStats.totalCached} images • {Math.round(cacheStats.totalSize / 1024 / 1024)}MB
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </CardContent>
          </Card>
        </div>

        {/* Custom Prompt Generator */}
        <Card className="glass-surface mb-8">
          <CardHeader>
            <CardTitle className="text-theme-text-primary">Custom Image Generator</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Input
                type="text"
                placeholder="Enter your custom prompt... (e.g., 'Modern health dashboard with biometric data visualization')"
                className="flex-1 bg-theme-surface-light border-theme-border text-theme-text-primary placeholder:text-theme-text-muted"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !isGenerating) {
                    generateCustomImage((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
              <Button
                onClick={(e) => {
                  const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
                  generateCustomImage(input.value);
                  input.value = '';
                }}
                disabled={isGenerating}
                className="bg-theme-primary hover:bg-theme-primary/90 text-theme-text-inverse"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Generate'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Images Grid/List */}
        <AnimatePresence mode="wait">
          {filteredImages.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-center py-16"
            >
              <ImageIcon className="w-16 h-16 text-theme-text-muted mx-auto mb-4" />
              <h3 className="text-xl text-theme-text-primary mb-2">No Images Generated Yet</h3>
              <p className="text-theme-text-secondary mb-6">
                Start by clicking "Generate Images" to create AI-powered visuals for your BioTune experience
              </p>
              <Button
                onClick={() => generateSectionImages()}
                disabled={isGenerating}
                className="bg-theme-primary hover:bg-theme-primary/90 text-theme-text-inverse"
              >
                {isGenerating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Generate Your First Images
              </Button>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={viewMode === 'grid' 
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
                : "space-y-4"
              }
            >
              {filteredImages.map((image, index) => (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={viewMode === 'list' ? "w-full" : ""}
                >
                  <Card className="glass-surface hover:shadow-xl transition-all duration-300 h-full">
                    <div className="relative group">
                      <img
                        src={image.url}
                        alt={image.altText}
                        className={`w-full object-cover ${viewMode === 'grid' ? 'h-48 rounded-t-lg' : 'h-32 rounded-l-lg'}`}
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center space-x-2">
                        <Button
                          size="sm"
                          onClick={() => setSelectedImage(image)}
                          className="bg-theme-primary hover:bg-theme-primary/90 text-theme-text-inverse"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => downloadImage(image)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => refreshImage(image)}
                          disabled={isGenerating}
                          className="bg-orange-600 hover:bg-orange-700 text-white"
                        >
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => deleteImage(image.id)}
                          variant="destructive"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                      <Badge 
                        className="absolute top-2 right-2 bg-blue-600/80 text-white text-xs"
                        variant="secondary"
                      >
                        {image.style}
                      </Badge>
                    </div>
                    
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <p className="text-sm text-theme-text-secondary line-clamp-2" title={image.prompt}>
                          {image.prompt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-theme-text-muted">
                          <div className="flex items-center space-x-1">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(image.timestamp).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <ImageIcon className="w-3 h-3" />
                            <span>{image.metadata.width}x{image.metadata.height}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Image Preview Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-theme-surface rounded-xl max-w-4xl max-h-[90vh] overflow-auto border border-theme-border"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-6">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.altText}
                    className="w-full h-auto rounded-lg mb-4"
                  />
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-theme-text-primary mb-2">Generation Details</h3>
                      <p className="text-theme-text-secondary text-sm mb-2">{selectedImage.prompt}</p>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-theme-text-muted">Style:</span>
                          <Badge className="ml-2" variant="secondary">{selectedImage.style}</Badge>
                        </div>
                        <div>
                          <span className="text-theme-text-muted">Size:</span>
                          <span className="ml-2 text-theme-text-primary">{selectedImage.metadata.width}x{selectedImage.metadata.height}</span>
                        </div>
                        <div>
                          <span className="text-theme-text-muted">Generated:</span>
                          <span className="ml-2 text-theme-text-primary">{new Date(selectedImage.timestamp).toLocaleDateString()}</span>
                        </div>
                        <div>
                          <span className="text-theme-text-muted">Model:</span>
                          <span className="ml-2 text-theme-text-primary">{selectedImage.metadata.model}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => downloadImage(selectedImage)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        onClick={() => refreshImage(selectedImage)}
                        disabled={isGenerating}
                        className="bg-orange-600 hover:bg-orange-700 text-white"
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerate
                      </Button>
                      <Button
                        onClick={() => {
                          deleteImage(selectedImage.id);
                          setSelectedImage(null);
                        }}
                        variant="destructive"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </Button>
                      <Button
                        onClick={() => setSelectedImage(null)}
                        variant="outline"
                        className="border-theme-border text-theme-text-primary hover:bg-theme-surface-light"
                      >
                        Close
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};